import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { useAnalytics, useCopyTextToClipboard, useNavigationConfig } from '@/hooks';

import { ReactComponent as ViewIcon } from '@/assets/icons/file.svg';
import { ReactComponent as DownloadIcon } from '@/assets/icons/download.svg';
import { ReactComponent as CopyIcon } from '@/assets/icons/copy.svg';
import { documentService } from '@/lib/services';

interface EventDocumentListItemProps {
	documentId: string;
	viewUrl: string;
	downloadUrl?: string;
	to: string;
	title: string;
}

export const EventDocumentListItem = ({ documentId, viewUrl, downloadUrl, to, title }: EventDocumentListItemProps) => {
	const { copyTextToClipboard } = useCopyTextToClipboard();
	const analytics = useAnalytics();
	const { navigationConfig } = useNavigationConfig();

	const handleShareButtonClick = () => {
		copyTextToClipboard(`${window.location.origin}/research/${documentId}`, 'Link copied to clipboard.');
	};

	const handleDownloadClick = useCallback(async () => {
		if (viewUrl) {
			try {
				await documentService.trackDocumentDownload(documentId).fetch();
				analytics.handleClickEvent(viewUrl);
			} catch (error) {
				// fail silently because we dont need to report a failed analytics call
			} finally {
				window.open(downloadUrl, '_blank', 'noopener');
			}
		}
	}, [viewUrl, documentId, analytics, downloadUrl]);

	return (
		<div className="py-4 d-flex align-items-center justify-content-between">
			<h3 className="mb-0">
				<Link to={to}>{title}</Link>
			</h3>
			<div className="d-flex align-items-center">
				<Button
					variant="link"
					className="mr-1"
					href={viewUrl}
					target="_blank"
					rel="noopener"
					onClick={() => {
						analytics.handleClickEvent(viewUrl);
					}}
				>
					<ViewIcon />
				</Button>
				{navigationConfig?.allowDownloads && downloadUrl && (
					<Button className="mr-1" variant="link" onClick={handleDownloadClick}>
						<DownloadIcon />
					</Button>
				)}
				<Button variant="link" className="mx-1" onClick={handleShareButtonClick}>
					<CopyIcon />
				</Button>
			</div>
		</div>
	);
};
